import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { DefaultService, GameFileInfo, OnlineRulesResult } from '../generated-api';
import {
  DocumentIcon,
  DocumentTextIcon,
  DocumentChartBarIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { useJobPoller } from '../hooks/useJobPoller';

interface FilesPageProps {
  gameId: string;
  gameName: string;
}

const FILE_CATEGORIES = [
  { value: 'Rules', label: 'Rules' },
  { value: 'Extension Rules', label: 'Extension Rules' },
  { value: 'Symbols', label: 'Symbols' },
  { value: 'Other', label: 'Other' },
];

const FilesPage: React.FC<FilesPageProps> = ({ gameId, gameName }) => {
  const [files, setFiles] = useState<GameFileInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [fileCategory, setFileCategory] = useState('Rules');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [onlineSuggestions, setOnlineSuggestions] = useState<OnlineRulesResult[]>([]);
  const [isSearchingOnline, setIsSearchingOnline] = useState(false);
  const [importingSlug, setImportingSlug] = useState<string | null>(null);

  const loadFiles = useCallback(async () => {
    try {
      setIsLoading(true);
      const filesData = await DefaultService.listGameFilesGamesGameIdRulesFilesGet({ gameId });
      setFiles(filesData);
    } catch (error) {
      console.error('Failed to load files:', error);
    } finally {
      setIsLoading(false);
    }
  }, [gameId]);

  const rulesPoller = useJobPoller(gameId, 'rules_processing', loadFiles);

  const strategyPoller = useJobPoller(gameId, 'strategy_generation', loadFiles);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  // Search for online rules on mount
  useEffect(() => {
    const searchOnline = async () => {
      setIsSearchingOnline(true);
      try {
        const results = await DefaultService.searchOnlineRulesGamesGameIdRulesSearchOnlineGet({
          gameId,
          query: gameName,
        });
        setOnlineSuggestions(results);
      } catch (error) {
        console.error('Failed to search online rules:', error);
      } finally {
        setIsSearchingOnline(false);
      }
    };
    searchOnline();
  }, [gameId, gameName]);

  const handleImportOnline = async (suggestion: OnlineRulesResult) => {
    setImportingSlug(suggestion.slug);
    try {
      const response = await DefaultService.importOnlineRulesGamesGameIdRulesImportOnlinePost({
        gameId,
        requestBody: { slug: suggestion.slug, name: suggestion.name },
      });
      rulesPoller.startJob(response.jobId);
      setOnlineSuggestions([]);
      await loadFiles();
    } catch (error: any) {
      console.error('Failed to import online rules:', error);
      setUploadError(error?.body?.detail || error.message || 'Failed to import rules.');
    } finally {
      setImportingSlug(null);
    }
  };

  // File upload handler
  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setUploadError(null);
    setIsUploading(true);

    try {
      // 1. Get presigned URL from backend
      const presignedUrlResponse = await DefaultService.getPresignedUrlForRulesPdfUploadGamesGameIdRulesUploadUrlPost({
        gameId,
        requestBody: {
          fileName: file.name,
          contentType: file.type,
          fileSize: file.size,
        },
      });

      // 2. Upload file directly to S3
      await fetch(presignedUrlResponse.url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      // 3. Notify backend of successful upload
      const jobResponse = await DefaultService.notifyRulesPdfUploadCompleteGamesGameIdRulesUploadPut({
        gameId,
        s3Key: presignedUrlResponse.s3_key,
        type: 'base',
      });

      // Start polling for the processing job
      rulesPoller.startJob(jobResponse.jobId);

      // Reload files to show the uploaded PDF
      await loadFiles();
    } catch (error: any) {
      console.error('Failed to upload PDF:', error);
      setUploadError(error?.body?.detail || error.message || 'Failed to upload PDF. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    maxSize: 30 * 1024 * 1024, // 30MB
    disabled: isUploading
  });

  const handleDeleteFile = async (fileId: string) => {
    try {
      await DefaultService.deleteGameFilesGamesGameIdRulesFilesDelete({
        gameId,
        requestBody: {
          fileIds: [fileId]
        }
      });

      setDeleteConfirm(null);
      await loadFiles();
    } catch (error: any) {
      console.error('Failed to delete file:', error);
      alert(error?.body?.detail || error.message || 'Failed to delete file. Please try again.');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'pdf':
        return <DocumentIcon className="h-6 w-6 text-red-500" />;
      case 'extracted_text':
        return <DocumentTextIcon className="h-6 w-6 text-primary-500" />;
      case 'summary':
        return <DocumentChartBarIcon className="h-6 w-6 text-green-500" />;
      default:
        return <DocumentIcon className="h-6 w-6 text-muted" />;
    }
  };

  const handleDownloadFile = async (file: GameFileInfo) => {
    try {
      // 1. Get presigned URL from backend
      const response = await DefaultService.getPresignedUrlForRulesPdfDownloadGamesGameIdRulesFilesFileIdDownloadUrlGet({
        gameId: file.gameId,
        fileId: file.fileId,
      });

      // 2. Open the URL in a new tab to trigger download
      window.open(response.url, '_blank');

    } catch (error: any) {
      console.error('Failed to get download URL:', error);
      alert(error?.body?.detail || error.message || 'Failed to get download link. Please try again.');
    }
  };

  const pdfFiles = files.filter(f => f.fileType === 'pdf');
  const extractedFiles = files.filter(f => f.fileType === 'extracted_text');
  const summaryFiles = files.filter(f => f.fileType === 'summary');

  const renderFileList = (fileList: GameFileInfo[], title: string, emptyMessage: string) => (
    <div className="mb-8">
      <h3 className="text-lg font-semibold text-heading mb-4">{title}</h3>
      {fileList.length === 0 ? (
        <p className="text-muted italic">{emptyMessage}</p>
      ) : (
        <div className="space-y-2">
          {fileList.map(file => (
            <div
              data-track="file_card"
              key={file.fileId}
              onClick={() => handleDownloadFile(file)}
              className="flex items-center justify-between p-4 bg-surface border border-edge rounded-xl hover:bg-surface-hover cursor-pointer"
            >
              <div className="flex items-center space-x-4 flex-1">
                {getFileIcon(file.fileType)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-heading truncate">
                    {file.fileName}
                  </p>
                  <p className="text-xs text-muted">
                    {formatFileSize(file.size)} • Uploaded {formatDate(file.uploadedAt)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {deleteConfirm === file.fileId ? (
                  <>
                    <button
                      onClick={() => handleDeleteFile(file.fileId)}
                      className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700"
                    >
                      Confirm Delete
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(null)}
                      className="px-3 py-1 text-xs font-medium text-body bg-surface-subtle rounded hover:bg-surface-skeleton"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setDeleteConfirm(file.fileId)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title="Delete file"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderProcessingCard = (statusText: string, error: string | null) => (
    <div className="mb-4 p-4 border border-primary-200 bg-primary-50 rounded-xl flex items-center gap-3">
      {error ? (
        <>
          <ExclamationTriangleIcon className="h-6 w-6 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </>
      ) : (
        <>
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 flex-shrink-0"></div>
          <span className="text-sm text-primary-700">{statusText}</span>
        </>
      )}
    </div>
  );

  return (
    <div className="max-w-4xl">
      {/* Upload Section */}
      <div className="bg-surface shadow rounded-xl p-6 mb-8">
        <h2 className="text-fluid-h2 font-semibold text-heading mb-4">Upload Rules</h2>

        {/* File Category Select */}
        <div className="mb-4">
          <label htmlFor="fileCategory" className="block text-sm font-medium text-body mb-2">
            Category
          </label>
          <select
            id="fileCategory"
            value={fileCategory}
            onChange={(e) => setFileCategory(e.target.value)}
            className="w-full px-3 py-2 border border-edge-input rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            disabled={isUploading}
          >
            {FILE_CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>

        {/* Dropzone */}
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors
            ${isDragActive ? 'border-primary-500 bg-primary-50' : 'border-edge-input hover:border-edge'}
            ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input {...getInputProps()} />
          <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-disabled" />
          <p className="mt-2 text-sm text-body">
            {isDragActive ? (
              <span className="text-primary-700 font-medium">Drop the PDF here</span>
            ) : (
              <>
                <span className="text-primary-700 font-medium">Click to upload</span> or drag and drop
              </>
            )}
          </p>
          <p className="mt-1 text-xs text-muted">PDF files up to 100MB</p>
        </div>

        {isUploading && (
          <div className="mt-4 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
            <span className="ml-2 text-sm text-body">Uploading...</span>
          </div>
        )}

        {uploadError && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-700">{uploadError}</p>
          </div>
        )}
      </div>

      {/* Online Rules Suggestions */}
      {onlineSuggestions.length > 0 && (
        <div className="bg-surface shadow rounded-xl p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <GlobeAltIcon className="h-5 w-5 text-primary-500" />
            <h2 className="text-fluid-h2 font-semibold text-heading">Find Rules Online</h2>
          </div>
          <p className="text-sm text-muted mb-4">
            We found matching rules on Ultra Board Games. Import them with one click.
          </p>
          <div className="space-y-2">
            {onlineSuggestions.map((suggestion) => (
              <div
                key={suggestion.slug}
                className="flex items-center justify-between p-3 border border-edge rounded-xl hover:bg-surface-hover"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-heading">{suggestion.name}</p>
                  <p className="text-xs text-muted truncate">{suggestion.url}</p>
                </div>
                <button
                  data-track="file_import_online"
                  onClick={() => handleImportOnline(suggestion)}
                  disabled={importingSlug !== null}
                  className="ml-4 px-4 py-2 text-sm font-medium text-white bg-primary-700 rounded-md hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {importingSlug === suggestion.slug ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Importing...
                    </>
                  ) : (
                    'Import'
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {isSearchingOnline && (
        <div className="bg-surface shadow rounded-xl p-6 mb-8 flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
          <span className="text-sm text-body">Searching for rules online...</span>
        </div>
      )}

      {/* Processing indicators */}
      {(rulesPoller.isProcessing || rulesPoller.error) &&
        renderProcessingCard(rulesPoller.statusText, rulesPoller.error)}
      {(strategyPoller.isProcessing || strategyPoller.error) &&
        renderProcessingCard(strategyPoller.statusText, strategyPoller.error)}

      {/* Files List */}
      <div className="bg-surface shadow rounded-xl p-6">
        <h2 className="text-fluid-h2 font-semibold text-heading mb-6">Uploaded Files</h2>

        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <>
            {renderFileList(pdfFiles, 'Original PDFs', 'No PDF files uploaded yet')}
            {renderFileList(extractedFiles, 'Extracted Rules', 'No extracted text files yet')}
            {renderFileList(summaryFiles, 'Generated Summaries', 'No summary files yet')}
          </>
        )}
      </div>
    </div>
  );
};

export default FilesPage;
